import React, { useState, useEffect, useRef } from "react";
import { Modal } from "react-bootstrap";
import {
  useClientGetChatsQuery,
  useSupportReplyMutation,
} from "../../redux/supportApi";
import config from "../../utils/config";
import { toast } from "react-toastify";

const { imageUrl } = config;

function SupportChatModal({ show, onClose, chatData }) {
  const { data: adminChats, refetch } = useClientGetChatsQuery(
    { ticket_id: chatData?.id },
    { skip: !show }
  );

  const [reply, { isLoading }] = useSupportReplyMutation();
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");


  // 🔑 Ref for auto-scroll
  const chatEndRef = useRef(null);

  // ✅ Auto scroll when chat opens or new messages load
  useEffect(() => {
    if (show && adminChats?.data?.length) {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [show, adminChats]);

  const handleClose = () => {
    setMessage("");
    setFile(null);
    setError("");
    onClose();
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!message.trim() && !file) {
      setError("This field is required");
      return;
    }

    const formData = new FormData();
    formData.append("ticket_id", chatData?.id);
    formData.append("message", message.trim());
    if (file) formData.append("attachment", file);

    try {
      const res = await reply(formData).unwrap();
      if (res?.status) {
        setMessage("");
        setFile(null);
        setError("");
        refetch();
      } else {
        toast.dismiss();
        toast.error(res?.message || "Failed to send message");
      }
    } catch (err) {
      toast.dismiss();
      toast.error(err?.data?.message || "Failed to send message");
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      className="account-popup-outer"
      backdrop="static"
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>Chat History - <span style={{wordBreak:'break-all'}}>{chatData?.subject}</span></Modal.Title>
      </Modal.Header>

      <Modal.Body
        style={{ maxHeight: "480px", minHeight: "240px", overflowY: "auto" }}
      >
        {adminChats &&
          adminChats?.data?.map((chat) => (
            <div
              key={chat.id}
              className={`d-flex mb-3 ${
                chat?.sender === "User"
                  ? "justify-content-end"
                  : "justify-content-start"
              }`}
            >
              <div
                className={`p-2 rounded shadow-sm ${
                  chat?.sender === "User"
                    ? "chat-color-user"
                    : "chat-color-admin"
                }`}
                style={{wordBreak:'break-all'}}
              >
                {chat?.message}
                {chat?.attachment && (
                  <div className="mt-1">
                    <a
                      href={imageUrl + chat?.attachment}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-decoration-underline text-primary"
                    >
                      <i className="fa fa-paperclip"></i>{" "}
                      {chat?.attachment?.split("/")?.pop()}
                    </a>
                  </div>
                )}
              </div>
            </div>
          ))}

        {/* 🔑 Scroll target */}
        <div ref={chatEndRef}></div>
      </Modal.Body>

      <Modal.Footer className="flex-column w-100">
        {chatData?.status && chatData?.status === "pending" ? (
          <form
            onSubmit={handleReply}
            className="d-flex w-100 align-items-center gap-2 mr-0"
          >
            <div className="flex-grow-1">
              <input
                type="text"
                className={`form-control rounded-pill ${
                  error ? "is-invalid" : ""
                }`}
                placeholder="Type Your Message..."
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  if (!e.target.value.trim()) setError("This field is required");
                  if (e.target.value.trim()) setError("");

                }}
              />
              {error && <div className="invalid-feedback ps-3">{error}</div>}
            </div>

            <input
              type="file"
              id="chatFile"
              className="d-none"
              onChange={(e) => setFile(e.target.files[0])}
            />
            <label
              htmlFor="chatFile"
              className="btn btn-outline-secondary rounded-circle send_btn mb-0"
            >
              <i className="fa fa-paperclip"></i>
            </label>
            {file ? <span>{file.name}</span> : <></>}
            <button
              type="submit"
              className="btn btn_man p-0 rounded-circle send_btn"
              disabled={isLoading}
            >
              <i className="fa fa-send"></i>
            </button>
          </form>
        ) : (
          <></>
        )}

        <button
          type="button"
          className="btn btn-outline-secondary mt-3 w-100 ml-0"
          onClick={handleClose}
        >
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export default SupportChatModal;
